import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Package, Clock, Users, Shield, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { industriesData } from "@/data/industriesData";
import { industryTranslations } from "@/data/industriesTranslations";
import { useLanguage } from "@/contexts/LanguageContext";

const Home = () => {
  const { language, t, getLocalizedPath } = useLanguage();
  
  const featuredIndustries = useMemo(() => {
    return industriesData.slice(0, 4).map(industryBase => {
        const translation = industryTranslations[industryBase.slug]?.[language] || industryTranslations[industryBase.slug]?.en;
        return {
            ...industryBase,
            ...translation,
        };
    });
  }, [language]);

  const whyChoose = [
    { icon: Package, titleKey: "whyChoose.wideRange.title", descKey: "whyChoose.wideRange.desc" },
    { icon: Clock, titleKey: "whyChoose.fastResponse.title", descKey: "whyChoose.fastResponse.desc" },
    { icon: Users, titleKey: "whyChoose.expertise.title", descKey: "whyChoose.expertise.desc" },
    { icon: Shield, titleKey: "whyChoose.reliable.title", descKey: "whyChoose.reliable.desc" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">{t("hero.title")}</h1>
              <p className="text-xl md:text-2xl mb-8 text-primary-foreground/90">{t("hero.subtitle")}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  <Link to={getLocalizedPath("contacts")}>{t("hero.cta.offer")}</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                  <Link to={getLocalizedPath("products")}>{t("hero.cta.products")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t("about.title")}</h2>
              <p className="text-lg text-muted-foreground mb-4">{t("about.text1")}</p>
              <p className="text-lg text-muted-foreground">{t("about.text2")}</p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("industries.title")}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t("industries.subtitle")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredIndustries.map((industry, index) => (
                  <Link key={index} to={getLocalizedPath(`industries/${industry.slug}`)}>
                    <Card className="border transition-all hover:shadow-lg hover:border-primary/50 h-full">
                      <CardContent className="p-6">
                        <industry.icon className="h-10 w-10 text-primary mb-4" />
                        <h3 className="text-xl font-semibold mb-2">{industry.title}</h3>
                        <p className="text-muted-foreground text-sm">{industry.description}</p>
                        <ArrowRight className="h-5 w-5 text-primary mt-4" />
                      </CardContent>
                    </Card>
                  </Link>
                )
              )}
            </div>
            <div className="text-center mt-8">
              <Button asChild variant="outline" size="lg">
                <Link to={getLocalizedPath("industries")}>{t("industries.viewAll")}<ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("productRange.title")}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">{t("productRange.subtitle")}</p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90">
              <Link to={getLocalizedPath("products")}>{t("productRange.cta")}<ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </section>

        <section className="py-16 bg-secondary">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">{t("whyChoose.title")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {whyChoose.map((item, index) => (
                <Card key={index} className="border bg-card">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                      <item.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{t(item.titleKey)}</h3>
                    <p className="text-sm text-muted-foreground">{t(item.descKey)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("cta.title")}</h2>
            <p className="text-lg mb-8 text-primary-foreground/90">{t("cta.subtitle")}</p>
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground">
              <Link to={getLocalizedPath("contacts")}>{t("cta.button")}</Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Home;
